import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Lock, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/errors";

export default function PermissionsPanel() {
  const [meta, setMeta] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/permissions").then(({ data }) => { setMeta(data); setMatrix(data.matrix); })
      .catch(e => toast.error(apiError(e)));
  }, []);

  if (!meta) return <p className="text-sm text-slate-500" data-testid="permissions-loading">Loading permissions…</p>;

  const toggle = (role, key, v) => setMatrix(m => ({ ...m, [role]: { ...m[role], [key]: v } }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/permissions", { roles: matrix });
      setMatrix(data.matrix); toast.success("Permissions saved — changes apply immediately");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-300 bg-white" data-testid="permissions-panel">
      <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-2xl">PERMISSIONS</h3>
          <p className="text-xs text-slate-500">Toggle what each role can access. Scoped roles only act within their assigned association, school or team.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setMatrix(meta.defaults)} data-testid="permissions-reset-defaults">
            <RotateCcw className="w-4 h-4 mr-2"/>Defaults
          </Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0A1128] hover:bg-slate-800 font-heading" data-testid="permissions-save">
            <Save className="w-4 h-4 mr-2"/>{saving ? "Saving..." : "SAVE"}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-100">
            <TableRow>
              <TableHead className="font-heading">Permission</TableHead>
              {meta.roles.map(r => <TableHead key={r.key} className="font-heading text-center whitespace-nowrap">{r.label}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {meta.permissions.map(p => (
              <TableRow key={p.key} data-testid={`perm-row-${p.key}`}>
                <TableCell>
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="font-overline text-[10px] text-slate-400">{p.group}</div>
                </TableCell>
                {meta.roles.map(r => (
                  <TableCell key={r.key} className="text-center">
                    {r.key === "super_admin" ? <Lock className="w-4 h-4 mx-auto text-slate-400"/> : (
                      <Switch checked={!!matrix[r.key]?.[p.key]} onCheckedChange={v => toggle(r.key, p.key, v)}
                        data-testid={`perm-${r.key}-${p.key}`}/>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell><div className="text-sm font-medium">Permissions panel</div><div className="font-overline text-[10px] text-slate-400">Administration</div></TableCell>
              {meta.roles.map(r => (
                <TableCell key={r.key} className="text-center text-xs text-slate-400">{r.key === "super_admin" ? <Lock className="w-4 h-4 mx-auto"/> : "—"}</TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
